/* eslint-disable @typescript-eslint/no-unused-vars */
import type { SavedProductStringTypes } from 'bundles/browse-common/types/SavedProductQueries';
import ProgramActionApiManager from 'bundles/enterprise-legacy-learner-home/models/ProgramActionApiManager';
import type { InjectedNaptime } from 'bundles/naptimejs';
import type { SaveProductLazyQueryFunc } from 'bundles/unified-description-page-common/providers/consumer/CourseraPlusSavedProductDataProvider';

type ProgramActionApiManagerConstructorArgs = {
  programId: string;
  naptime: InjectedNaptime;
  userId: number;
};

type ConsumerConstructorArgs = {
  productType: SavedProductStringTypes;
  saveProduct: SaveProductLazyQueryFunc;
  unSaveProduct: SaveProductLazyQueryFunc;
};

export class SaveProductManager {
  async save(id: string) {
    throw new Error('Implement me!');
  }

  async unSave(id: string) {
    throw new Error('Implement me!');
  }
}

class EnterpriseCourseSaveProductManagerImpl implements SaveProductManager {
  declare programApiActionManager: ProgramActionApiManager;

  constructor(programId: string, naptime: InjectedNaptime, userId: number) {
    this.programApiActionManager = new ProgramActionApiManager({ programId, naptime, userId });
  }

  async save(id: string) {
    try {
      await this.programApiActionManager.getSelectCoursePromise({ courseId: id, collectionId: null });
    } catch (err) {
      throw err;
    }
  }

  async unSave(id: string) {
    try {
      await this.programApiActionManager.getUnselectCoursePromise({ courseId: id, collectionId: null });
    } catch (err) {
      throw err;
    }
  }
}

class EnterpriseSpecializationSaveProductManagerImpl implements SaveProductManager {
  declare programApiActionManager: ProgramActionApiManager;

  constructor(programId: string, naptime: InjectedNaptime, userId: number) {
    this.programApiActionManager = new ProgramActionApiManager({ programId, naptime, userId });
  }

  async save(id: string) {
    try {
      await this.programApiActionManager.getSelectS12nPromise({ s12nId: id, collectionId: null });
    } catch (err) {
      throw err;
    }
  }

  async unSave(id: string) {
    try {
      await this.programApiActionManager.getUnselectS12nPromise({ s12nId: id, collectionId: null });
    } catch (err) {
      throw err;
    }
  }
}

// This is only available for Coursera Plus users.
class ConsumerSaveProductManagerImpl implements SaveProductManager {
  declare productType: SavedProductStringTypes;

  declare saveProduct: SaveProductLazyQueryFunc;

  declare unSaveProduct: SaveProductLazyQueryFunc;

  constructor(
    productType: SavedProductStringTypes,
    saveProduct: SaveProductLazyQueryFunc,
    unSaveProduct: SaveProductLazyQueryFunc
  ) {
    this.productType = productType;
    this.saveProduct = saveProduct;
    this.unSaveProduct = unSaveProduct;
  }

  async save(id: string) {
    try {
      await new Promise((resolve, reject) => {
        this.saveProduct({
          variables: {
            input: {
              product: {
                // eslint-disable-next-line camelcase
                product_id: id,
                // eslint-disable-next-line camelcase
                product_type: this.productType,
              },
            },
          },
          onCompleted: resolve,
          onError: reject,
        });
      });
    } catch (err) {
      throw err;
    }
  }

  async unSave(id: string) {
    try {
      await new Promise((resolve, reject) => {
        this.unSaveProduct({
          variables: {
            input: {
              product: {
                // eslint-disable-next-line camelcase
                product_id: id,
                // eslint-disable-next-line camelcase
                product_type: this.productType,
              },
            },
          },
          onCompleted: resolve,
          onError: reject,
        });
      });
    } catch (err) {
      throw err;
    }
  }
}

export const saveProductFactory = {
  getEnterpriseCourseSaveProduct({ programId, naptime, userId }: ProgramActionApiManagerConstructorArgs) {
    return new EnterpriseCourseSaveProductManagerImpl(programId, naptime, userId);
  },
  getEnterpriseSpecializationSaveProduct({ programId, naptime, userId }: ProgramActionApiManagerConstructorArgs) {
    return new EnterpriseSpecializationSaveProductManagerImpl(programId, naptime, userId);
  },
  getConsumerSaveProduct({ productType, saveProduct, unSaveProduct }: ConsumerConstructorArgs) {
    return new ConsumerSaveProductManagerImpl(productType, saveProduct, unSaveProduct);
  },
};
